// ============================================================================
// BESPOKIAN Pharmacist Cockpit - Status Badge
// Prescription workflow status indicator
// ============================================================================

import { clsx } from 'clsx';
import type { PrescriptionStatus } from '@/types';

interface StatusBadgeProps {
  status: PrescriptionStatus;
  className?: string;
}

const STATUS_CONFIG: Record<
  PrescriptionStatus,
  { label: string; color: string }
> = {
  RECEIVED: { label: 'Received', color: 'bg-gray-100 text-gray-700' },
  PENDING_VERIFICATION: {
    label: 'Pending Verify',
    color: 'bg-yellow-100 text-yellow-800',
  },
  VERIFIED: { label: 'Verified', color: 'bg-blue-100 text-blue-800' },
  PENDING_DUR: { label: 'DUR Review', color: 'bg-orange-100 text-orange-800' },
  DUR_PASSED: { label: 'DUR Clear', color: 'bg-green-100 text-green-800' },
  DUR_OVERRIDE: { label: 'DUR Override', color: 'bg-amber-100 text-amber-800' },
  PENDING_FILL: { label: 'To Fill', color: 'bg-purple-100 text-purple-800' },
  FILLING: { label: 'Filling', color: 'bg-purple-200 text-purple-900' },
  FILLED: { label: 'Filled', color: 'bg-indigo-100 text-indigo-800' },
  PENDING_PHARMACIST_CHECK: {
    label: 'RPh Check',
    color: 'bg-teal-100 text-teal-800',
  },
  CHECKED: { label: 'Checked', color: 'bg-emerald-100 text-emerald-800' },
  READY_FOR_PICKUP: {
    label: 'Ready',
    color: 'bg-green-500 text-white',
  },
  PICKED_UP: { label: 'Picked Up', color: 'bg-gray-500 text-white' },
  SHIPPED: { label: 'Shipped', color: 'bg-blue-500 text-white' },
  CANCELLED: { label: 'Cancelled', color: 'bg-gray-400 text-white' },
  REJECTED: { label: 'Rejected', color: 'bg-red-500 text-white' },
};

export function StatusBadge({ status, className }: StatusBadgeProps) {
  const config = STATUS_CONFIG[status] || {
    label: status,
    color: 'bg-gray-100 text-gray-700',
  };

  return (
    <span
      className={clsx(
        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
        config.color,
        className
      )}
    >
      {config.label}
    </span>
  );
}

// Get next status in workflow
export function getNextStatus(
  current: PrescriptionStatus
): PrescriptionStatus | null {
  const workflow: Record<PrescriptionStatus, PrescriptionStatus | null> = {
    RECEIVED: 'PENDING_VERIFICATION',
    PENDING_VERIFICATION: 'VERIFIED',
    VERIFIED: 'PENDING_DUR',
    PENDING_DUR: 'DUR_PASSED',
    DUR_PASSED: 'PENDING_FILL',
    DUR_OVERRIDE: 'PENDING_FILL',
    PENDING_FILL: 'FILLING',
    FILLING: 'FILLED',
    FILLED: 'PENDING_PHARMACIST_CHECK',
    PENDING_PHARMACIST_CHECK: 'CHECKED',
    CHECKED: 'READY_FOR_PICKUP',
    READY_FOR_PICKUP: null,
    PICKED_UP: null,
    SHIPPED: null,
    CANCELLED: null,
    REJECTED: null,
  };

  return workflow[current];
}
